import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, Mail, User, Key, AlertCircle, Loader2, CheckCircle, Shield, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminSetupPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useStore();
  
  const [mode, setMode] = useState('check'); // 'check', 'setup', 'reset'
  const [adminInfo, setAdminInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    setupKey: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Vérifier si un admin existe déjà
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/info`);
        const data = await response.json();
        
        if (data.exists) {
          setAdminInfo(data);
          setMode('reset');
        } else {
          setMode('setup');
        }
      } catch (err) {
        console.error('Error checking admin:', err);
        setMode('setup');
      }
    };
    checkAdminExists();
  }, []);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/shop');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          setup_key: formData.setupKey
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Compte admin créé avec succès !');
        if (data.token) {
          const userWithRole = { ...data.user, role: 'admin' };
          setUser(userWithRole, data.token);
          setTimeout(() => navigate('/admin'), 1500);
        } else {
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        setError(data.detail || 'Erreur lors de la création du compte');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          new_password: formData.password,
          reset_key: formData.setupKey
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Mot de passe réinitialisé avec succès !');
        if (data.token) {
          const userWithRole = { ...data.user, role: 'admin' };
          setUser(userWithRole, data.token);
          setTimeout(() => navigate('/admin'), 1500);
        } else {
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        setError(data.detail || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Chargement
  if (mode === 'check') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black" />
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        
        {/* Logo */}
        <div className="mb-6 relative">
          <div className="absolute inset-0 blur-3xl bg-amber-500/10 rounded-full transform scale-150" />
          <img
            src="/logo-ebb.png"
            alt="EBB Sportswear Store"
            className={`relative w-48 h-auto transition-all duration-1000 ${
              logoLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
            onLoad={() => setLogoLoaded(true)}
            style={{ filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.3))' }}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-4">
            {mode === 'reset' ? (
              <RefreshCw className="w-4 h-4 text-amber-500" />
            ) : (
              <Shield className="w-4 h-4 text-amber-500" />
            )}
            <span className="text-amber-500 text-sm font-medium">
              {mode === 'reset' ? 'Récupération Admin' : 'Configuration Initiale'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white">
            {mode === 'reset' ? 'Réinitialiser le mot de passe' : 'Créer le compte Admin'}
          </h1>
          {mode === 'reset' && adminInfo && (
            <p className="text-gray-400 text-sm mt-2">
              Compte existant : <span className="text-amber-500">{adminInfo.email}</span>
            </p>
          )}
        </div>

        {/* Form Card */}
        <div className="w-full bg-gradient-to-b from-gray-900/90 to-gray-950/90 backdrop-blur-xl rounded-2xl p-6 border border-amber-600/20 shadow-2xl">
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          <form onSubmit={mode === 'reset' ? handleReset : handleSetup} className="space-y-4">
            
            {/* Name Fields - Only for setup */}
            {mode === 'setup' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="firstName" className="text-gray-300 text-sm">Prénom</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Prénom"
                      required
                      className="pl-10 h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lastName" className="text-gray-300 text-sm">Nom</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Nom"
                    required
                    className="h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1">
              <Label htmlFor="email" className="text-gray-300 text-sm">
                {mode === 'reset' ? 'Email du compte admin' : 'Email'}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={mode === 'reset' ? 'Entrez l\'email complet' : 'admin@ebb-shop.be'}
                  required
                  className="pl-10 h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1">
              <Label htmlFor="password" className="text-gray-300 text-sm">
                {mode === 'reset' ? 'Nouveau mot de passe' : 'Mot de passe'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 caractères"
                  required
                  minLength={8}
                  className="pl-10 pr-10 h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1">
              <Label htmlFor="confirmPassword" className="text-gray-300 text-sm">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirmer le mot de passe"
                  required
                  className="pl-10 h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                />
              </div>
            </div>

            {/* Setup Key */}
            <div className="space-y-1">
              <Label htmlFor="setupKey" className="text-gray-300 text-sm">
                Clé de sécurité
              </Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="setupKey"
                  name="setupKey"
                  type="password"
                  value={formData.setupKey}
                  onChange={handleChange}
                  placeholder="ebb-admin-setup-2024"
                  required
                  className="pl-10 h-10 bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 text-sm"
                />
              </div>
              <p className="text-gray-500 text-xs mt-1">
                Clé par défaut : <code className="text-amber-500">ebb-admin-setup-2024</code>
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-amber-600/25 hover:shadow-amber-500/40 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {mode === 'reset' ? 'Réinitialisation...' : 'Création...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'reset' ? <RefreshCw className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                  {mode === 'reset' ? 'Réinitialiser le mot de passe' : 'Créer le compte Admin'}
                </span>
              )}
            </Button>
          </form>

          {/* Link to login */}
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-amber-500 text-sm hover:text-amber-400 transition-colors"
            >
              ← Retour à la connexion
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-xs">© {new Date().getFullYear()} EBB Sportswear Store</p>
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-amber-600/20 rounded-tl-3xl" />
      <div className="absolute top-0 right-0 w-24 h-24 border-r-2 border-t-2 border-amber-600/20 rounded-tr-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 border-l-2 border-b-2 border-amber-600/20 rounded-bl-3xl" />
      <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-amber-600/20 rounded-br-3xl" />
    </div>
  );
};

export default AdminSetupPage;
